/**
 * vistaDelDocumento.js — cuándo se sube el acta y cuándo se enseña.
 *
 * Tres decisiones sacadas de `VistaWord`. No están aquí por ordenar: están aquí
 * porque `VistaWord` lleva JSX y una prueba de Node no puede importarlo. La
 * alternativa era copiar el cuerpo de la función dentro de la prueba —que es lo
 * que se hacía—, y una copia de la que depende el arreglo de un fallo se
 * desfasa el día que alguien toque la original sin acordarse de la otra.
 *
 * ── Subir y enseñar son dos cosas distintas ───────────────────────────────
 *
 * Iban juntas: en cuanto el acta se volvía a subir, el recuadro de Word se
 * recargaba con ella. Y recargar el recuadro recarga Word entero, que tarda sus
 * segundos y se lleva el cursor —los campos que se corrigen están justo al
 * lado—. Corregir diez campos eran diez interrupciones a mitad de palabra.
 *
 * Ahora SUBIR sigue siendo automático y ENSEÑAR se pide. Lo primero no es
 * comodidad: «Enviar a firmas» y el PDF se fabrican a partir del archivo que
 * está arriba, así que dejar de subirlo mandaría a firmar una versión atrasada
 * sin que nadie lo note.
 */

/**
 * ¿Hay algo escrito en la aplicación que todavía no está en OneDrive?
 *
 * Es la pregunta que decide si se puede adoptar lo que diga el archivo.
 * Mientras la respuesta sea que sí, el archivo de OneDrive es una copia
 * ATRASADA de lo que la persona tiene delante: traerlo no sería ponerse al día,
 * sería deshacer lo último que escribió. Eso pasaba.
 *
 * Sin `base64` no hay acta todavía y no hay nada que proteger. Invertir esto
 * por descuido vuelve a traer el fallo entero.
 */
export function hayCambiosSinSubir({ subiendo, publicado, base64 }) {
  if (subiendo) return true;
  if (!base64) return false;
  return publicado !== base64;
}

/**
 * ¿Qué sesión de Word se enseña, de la que había y la que acaba de subir?
 *
 * Aquí se decide que el documento NO se recargue solo cada vez que se corrige
 * un campo. Tres casos, y ninguno es de adorno:
 *
 *   · No había nada en pantalla → la nueva, sin pedirla. Es abrir la reunión, y
 *     un botón «ver el acta» sobre un hueco vacío sería pedirle a alguien que
 *     pulse para que empiece lo que venía a hacer.
 *   · Se pidió ver lo último → la nueva. Se pulsó el botón mientras el acta
 *     subía, y esto es lo que atiende la petición cuando llega.
 *   · Ni lo uno ni lo otro → la que ya estaba. Recargar aquí es lo que tardaba
 *     y robaba el cursor.
 *
 * Invertir el tercer caso devuelve el problema entero sin romper nada visible,
 * así que se prueba aparte.
 */
export function queEnsenar({ enPantalla, nueva, pedido }) {
  if (!enPantalla) return nueva;
  return pedido ? nueva : enPantalla;
}

/**
 * ¿Hay que ofrecer el botón de ver el documento al día?
 *
 * Solo cuando lo que se ve NO es lo último: o porque hay una versión subida que
 * todavía no se enseña, o porque lo recién corregido va de camino a SharePoint.
 *
 * Con la pantalla vacía nunca. Entonces no hay un «al día» frente a un
 * «atrasado»: hay un documento abriéndose, y eso se dice con otras palabras.
 */
export function hayQueOfrecerVer({ url, enPantalla, base64, asentado, subiendo }) {
  if (!enPantalla) return false;
  if (url && url !== enPantalla) return true;
  return Boolean(subiendo) || Boolean(base64 && asentado !== base64);
}
